/**
 * TagPage — listing of businesses for a single tag (item #7/#8).
 *
 * URL: /:municipioSlug?/tags/:tagSlug
 * Features:
 *   - Header com label da tag + total de negócios.
 *   - Campo de busca interna pra refinar resultados.
 *   - Grid de BusinessCard.
 *   - Empty state amigável.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tag as TagIcon, Search, Loader2, ChevronLeft, Frown } from 'lucide-react';
import BusinessCard from '../components/BusinessCard';
import Breadcrumbs from '../components/Breadcrumbs';
import { api } from '../services/api';

export default function TagPage() {
  const { tagSlug, municipioSlug } = useParams();
  const [tag, setTag] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search input so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadTag = useCallback(async () => {
    if (!tagSlug) return;
    try {
      const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
      const { data } = await api.get(`/tags/${tagSlug}`, { params });
      setTag(data);
    } catch (e) {
      setTag({ slug: tagSlug, label: tagSlug, business_count: 0, _notFound: true });
    }
  }, [tagSlug, municipioSlug]);

  const loadBusinesses = useCallback(async () => {
    if (!tagSlug) return;
    try {
      setLoading(true);
      const params = { limit: 60 };
      if (municipioSlug) params.municipio_slug = municipioSlug;
      if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
      const { data } = await api.get(`/tags/${tagSlug}/businesses`, { params });
      setBusinesses(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      setBusinesses([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tagSlug, municipioSlug, debouncedQuery]);

  useEffect(() => { loadTag(); }, [loadTag]);
  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);

  const tagsHomeLink = municipioSlug ? `/${municipioSlug}/tags` : '/tags';
  const headline = tag?.label || tagSlug;

  const breadcrumbItems = useMemo(() => ([
    { label: 'Tags', path: tagsHomeLink },
    { label: headline, path: null },
  ]), [tagsHomeLink, headline]);

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)]" data-testid="tag-page">
      <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <Breadcrumbs items={breadcrumbItems} className="mb-2" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <header className="flex items-start gap-3 flex-wrap mb-6">
          <Link
            to={tagsHomeLink}
            className="text-sm text-[var(--primary)] hover:underline inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Todas as tags
          </Link>
        </header>

        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mb-2">
              <TagIcon className="w-3.5 h-3.5" /> Tag
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }} data-testid="tag-page-headline">
              {headline}
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-testid="tag-page-count">
              {total} negócio{total === 1 ? '' : 's'} encontrado{total === 1 ? '' : 's'}
              {municipioSlug ? <> em <strong>{municipioSlug.replace(/-/g, ' ')}</strong></> : null}
            </p>
          </div>

          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar nesta tag..."
              className="w-full pl-10 pr-4 py-3 border border-[var(--border)] rounded-full bg-white focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] text-sm"
              data-testid="tag-page-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-20" data-testid="tag-page-empty">
            <Frown className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">
              Nenhum negócio com esta tag{query ? <> para "{query}"</> : null}.
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {query ? 'Tente outro termo de busca' : 'Volte mais tarde — empreendedores estão se cadastrando todo dia.'}
            </p>
            <Link
              to={tagsHomeLink}
              className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[var(--primary)] hover:underline"
            >
              ← Explorar outras tags
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
            data-testid="tag-page-grid"
          >
            {businesses.map((b) => (
              <BusinessCard key={b.id} business={b} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
